// ============================================================================
// Tailor panel — "Customize" slide-out drawer (right panel on desktop,
// bottom-sheet-ish full-width on mobile, mirroring the SOP detail drawer
// pattern). Lists every widget the viewer's role may see, in layout order,
// with: show/hide checkbox, drag handle (dnd-kit vertical list), and up/down
// arrow buttons (mobile + keyboard fallback for the drag).
//
// Changes apply instantly (optimistic) via onChange — there's no separate
// Save step. Footer: Reset to default.
// ============================================================================

import { useEffect } from 'react';
import { ArrowDown, ArrowUp, GripVertical, RotateCcw, X } from 'lucide-react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '../../shared/components';
import {
  WIDGET_BY_KEY,
  type LayoutEntry,
  type WidgetDef,
} from './widget-registry';
import { overlayClickClose } from '../../shared/overlay-close';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Full layout (visible + hidden) for the viewer's role, in order. */
  layout: LayoutEntry[];
  onChange: (next: LayoutEntry[]) => void;
  onReset: () => void;
  hasCustom: boolean;
  saveError: string | null;
}

export function TailorPanel({
  open,
  onClose,
  layout,
  onChange,
  onReset,
  hasCustom,
  saveError,
}: Props) {
  // Esc closes — registered only while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 8 },
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  if (!open) return null;

  // Rows = layout entries that resolve in the registry (resolveLayout already
  // dropped unknown/forbidden keys, so this is belt-and-braces).
  const rows = layout
    .map((entry) => ({ entry, def: WIDGET_BY_KEY.get(entry.widget_key) }))
    .filter((r): r is { entry: LayoutEntry; def: WidgetDef } => !!r.def);

  const toggle = (key: string) => {
    onChange(
      layout.map((e) =>
        e.widget_key === key ? { ...e, visible: !e.visible } : e,
      ),
    );
  };

  const move = (key: string, delta: -1 | 1) => {
    const from = layout.findIndex((e) => e.widget_key === key);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= layout.length) return;
    onChange(arrayMove(layout, from, to));
  };

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = layout.findIndex((e) => e.widget_key === active.id);
    const to = layout.findIndex((e) => e.widget_key === over.id);
    if (from < 0 || to < 0) return;
    onChange(arrayMove(layout, from, to));
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-end bg-tcs-scrim/60 sm:items-stretch"
      {...overlayClickClose(onClose)}
      role="dialog"
      aria-modal="true"
      aria-label="Tailor my dashboard"
    >
      <div
        className="flex h-[85vh] w-full flex-col rounded-t-2xl bg-tcs-overlay shadow-xl sm:h-full sm:w-[400px] sm:rounded-none"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between border-b border-tcs-border p-4">
          <div>
            <h2 className="text-base font-semibold text-tcs-text">
              Tailor my dashboard
            </h2>
            <p className="mt-0.5 text-xs text-tcs-text-muted">
              Check what you want to see; drag (or use the arrows) to set the
              order. Changes save automatically.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1.5 text-tcs-text-muted hover:bg-tcs-surface hover:text-tcs-text-secondary"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {saveError && (
          <p className="border-b border-st-danger-border bg-st-danger-bg px-4 py-2 text-xs text-st-danger-fg">
            Couldn't save your layout: {saveError}
          </p>
        )}

        <div className="flex-1 overflow-y-auto p-3">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onDragEnd}
          >
            <SortableContext
              items={rows.map((r) => r.entry.widget_key)}
              strategy={verticalListSortingStrategy}
            >
              <ul className="space-y-1.5">
                {rows.map((r, i) => (
                  <PanelRow
                    key={r.entry.widget_key}
                    entry={r.entry}
                    def={r.def}
                    isFirst={i === 0}
                    isLast={i === rows.length - 1}
                    onToggle={() => toggle(r.entry.widget_key)}
                    onMoveUp={() => move(r.entry.widget_key, -1)}
                    onMoveDown={() => move(r.entry.widget_key, 1)}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        </div>

        <footer className="flex items-center justify-between border-t border-tcs-border p-4">
          <Button
            variant="secondary"
            size="sm"
            onClick={onReset}
            disabled={!hasCustom}
            icon={<RotateCcw className="h-3.5 w-3.5" />}
          >
            Reset to default
          </Button>
          <Button variant="primary" size="sm" onClick={onClose}>
            Done
          </Button>
        </footer>
      </div>
    </div>
  );
}

// ----- Sortable row -----------------------------------------------------------

interface RowProps {
  entry: LayoutEntry;
  def: WidgetDef;
  isFirst: boolean;
  isLast: boolean;
  onToggle: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}

function PanelRow({
  entry,
  def,
  isFirst,
  isLast,
  onToggle,
  onMoveUp,
  onMoveDown,
}: RowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: entry.widget_key });

  return (
    <li
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      className={`flex items-center gap-2 rounded-lg border p-2.5 ${
        isDragging
          ? 'z-10 border-tcs-border-strong bg-tcs-raised shadow-md'
          : 'border-tcs-border bg-tcs-raised'
      }`}
    >
      <button
        type="button"
        className="shrink-0 cursor-grab touch-none rounded p-1 text-tcs-text-faint hover:text-tcs-text-muted active:cursor-grabbing"
        aria-label={`Drag to reorder ${def.name}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>

      <label className="flex min-w-0 flex-1 cursor-pointer items-start gap-2.5">
        <input
          type="checkbox"
          checked={entry.visible}
          onChange={onToggle}
          className="mt-0.5 h-4 w-4 shrink-0 rounded border-tcs-border-strong"
        />
        <span className="min-w-0">
          <span
            className={`block text-sm font-medium ${
              entry.visible ? 'text-tcs-text' : 'text-tcs-text-muted'
            }`}
          >
            {def.name}
          </span>
          <span className="block truncate text-xs text-tcs-text-muted">
            {def.description}
          </span>
        </span>
      </label>

      <div className="flex shrink-0 flex-col">
        <button
          type="button"
          onClick={onMoveUp}
          disabled={isFirst}
          className="rounded p-0.5 text-tcs-text-muted hover:text-tcs-text-secondary disabled:opacity-30"
          aria-label={`Move ${def.name} up`}
        >
          <ArrowUp className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={onMoveDown}
          disabled={isLast}
          className="rounded p-0.5 text-tcs-text-muted hover:text-tcs-text-secondary disabled:opacity-30"
          aria-label={`Move ${def.name} down`}
        >
          <ArrowDown className="h-3.5 w-3.5" />
        </button>
      </div>
    </li>
  );
}
